import type { FC, ReactNode } from "react";
import { cn } from "./utils/cn";

export type AlertVariant = "info" | "success" | "warning" | "danger";

export interface AlertProps {
  variant?: AlertVariant;
  title?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  className?: string;
}

const variantClasses: Record<AlertVariant, string> = {
  info: "bg-secondary/10 text-menu border-secondary/30",
  success: "bg-success/10 text-menu border-success/30",
  warning: "bg-danger-soft/10 text-menu border-danger-soft/30",
  danger: "bg-danger/10 text-menu border-danger/30",
};

/**
 * Inline status banner. Replaces MUI `<Alert>` with one tokenized look that
 * dark-modes via the surface/feedback tokens.
 */
const Alert: FC<AlertProps> = ({
  variant = "info",
  title,
  children,
  icon,
  className,
}) => {
  return (
    <div
      role="alert"
      className={cn(
        "flex items-start gap-3 rounded-card border px-4 py-3.5 text-sm",
        variantClasses[variant],
        className
      )}
    >
      {icon && <span className="mt-0.5 shrink-0">{icon}</span>}
      <div className="min-w-0">
        {title && <p className="font-semibold">{title}</p>}
        {children && (
          <div className={cn(title && "mt-0.5 text-menu/75")}>{children}</div>
        )}
      </div>
    </div>
  );
};

export default Alert;
